import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.id !== article.id && a.category === article.category)
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading text-foreground-900 mb-4">文章不存在</h1>
          <Link to="/articles" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回文章中心</Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '九色导航',
      url: 'https://www.jiusenavigation.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.jiusenavigation.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} | 九色导航`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        schemaJson={articleSchema}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '文章中心', href: '/articles' },
                  { label: article.title },
                ]}
              />
            </div>

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-sm text-foreground-400">{article.publishDate}</span>
                <span className="text-sm text-foreground-400">· 阅读{article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>

              <p className="text-sm text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-md bg-background-100 text-foreground-500 whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full object-cover"
                title={`${article.title} - 九色导航`}
              />
            </div>

            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-16 pt-10 border-t border-background-200">
                <h3 className="text-lg font-heading text-foreground-950 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group p-4 rounded-xl bg-background-100/50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-400">{ra.publishDate} · 阅读{ra.readTime}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}